import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { AcornPlaceholder, TYPE_LABEL, absMediaUrl } from "@/components/AcornPlaceholder";
import { GlassWater, Grape, Utensils, MapPin, ExternalLink } from "lucide-react";
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogHeader,
    DialogTitle,
} from "@/components/ui/dialog";
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "@/components/ui/select";

// Kept in sync with backend/models.NON_WINE_CATEGORIES
const CATEGORIA_LABEL = {
    vino: null,        // fall back to type badge for wines
    distillato: "Distillato",
    liquore: "Liquore",
    birra: "Birra",
    altro: "Altro",
};

export default function WineDetailDialog({ wineId, open, onOpenChange, canSeePrices, unverifiedNotice, isTradeVipUnverified }) {
    const [wine, setWine] = useState(null);
    const [loading, setLoading] = useState(false);
    const [selectedFormat, setSelectedFormat] = useState(null);

    useEffect(() => {
        if (!open || !wineId) return;
        setLoading(true);
        setWine(null);
        api
            .get(`/wines/${wineId}`)
            .then(({ data }) => {
                setWine(data);
                setSelectedFormat(data.formats?.[0]?.size || null);
            })
            .catch(() => setWine(null))
            .finally(() => setLoading(false));
    }, [open, wineId]);

    const currentFmt = wine?.formats?.find((f) => f.size === selectedFormat);
    // Suppress €0 in the detail dialog — treat as "no price yet".
    const currentPrice = currentFmt && typeof currentFmt.price === "number" && currentFmt.price > 0
        ? currentFmt.price
        : null;
    const cardUrl = absMediaUrl(wine?.images?.card_url);
    const isNonWine = wine && wine.categoria && wine.categoria !== "vino";
    const badgeLabel = isNonWine
        ? (CATEGORIA_LABEL[wine.categoria] || "Distillato")
        : (wine ? TYPE_LABEL[wine.type] : "");

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent
                className="sm:max-w-2xl max-h-[95vh] overflow-y-auto p-0 gap-0 bg-white"
                data-testid="wine-detail-dialog"
            >
                {loading || !wine ? (
                    <>
                        <DialogHeader className="sr-only">
                            <DialogTitle>Dettaglio vino</DialogTitle>
                            <DialogDescription>Caricamento dei dettagli del vino selezionato.</DialogDescription>
                        </DialogHeader>
                        <div className="p-10 text-center text-[hsl(var(--muted-foreground))]">
                            Caricamento…
                        </div>
                    </>
                ) : (
                    <>
                        <DialogHeader className="p-6 sm:p-8 pb-4 border-b border-[hsl(var(--border))]">
                            <div className="flex items-start gap-5">
                                {cardUrl ? (
                                    <img
                                        src={cardUrl}
                                        alt={wine.name}
                                        loading="lazy"
                                        className="w-24 h-auto max-h-40 object-contain shrink-0"
                                    />
                                ) : (
                                    <AcornPlaceholder size={90} opacity={0.55} />
                                )}
                                <div className="flex-1 min-w-0">
                                    <div className="flex flex-wrap items-center gap-2 mb-2">
                                        <span className="co-caption">{badgeLabel}</span>
                                        {wine.glass_available && (
                                            <span className="co-badge-calice">
                                                <GlassWater className="w-3 h-3" strokeWidth={1.5} />
                                                Al Calice
                                            </span>
                                        )}
                                    </div>
                                    {/* FIX 8.9j-UI · gerarchia invertita: producer dominante */}
                                    <DialogTitle className="text-base sm:text-lg leading-tight text-left">
                                        {wine.name}
                                        {wine.vintage && !isNonWine && (
                                            <span className="ml-2 italic text-[hsl(var(--muted-foreground))]">
                                                {wine.vintage}
                                            </span>
                                        )}
                                    </DialogTitle>
                                    {wine.producer && wine.producer !== "—" && (
                                        <p className="mt-2 text-3xl sm:text-4xl leading-tight text-[hsl(var(--muted-foreground))]">
                                            {wine.producer}
                                        </p>
                                    )}
                                </div>
                            </div>
                        </DialogHeader>

                        <div className="p-6 sm:p-8 space-y-6">
                            <div className="flex items-start gap-2 text-sm text-[hsl(var(--muted-foreground))]">
                                <MapPin className="w-4 h-4 mt-0.5 shrink-0" strokeWidth={1.5} />
                                <div>
                                    <div>
                                        {wine.region} · {wine.country}
                                    </div>
                                    {wine.denomination && (
                                        <div className="italic mt-1">{wine.denomination}</div>
                                    )}
                                </div>
                            </div>

                            {wine.organoleptic?.grapes?.length > 0 && !isNonWine && (
                                <div className="flex items-start gap-2 text-sm">
                                    <Grape className="w-4 h-4 mt-0.5 shrink-0 text-[hsl(var(--primary))]" strokeWidth={1.5} />
                                    <div>
                                        <div className="co-caption mb-1">Vitigni</div>
                                        <div>{wine.organoleptic.grapes.join(", ")}</div>
                                    </div>
                                </div>
                            )}

                            {wine.organoleptic?.tasting_notes && (
                                <div>
                                    <div className="co-caption mb-2">Note Organolettiche</div>
                                    <p className="text-sm leading-relaxed">
                                        {wine.organoleptic.tasting_notes}
                                    </p>
                                </div>
                            )}

                            {wine.organoleptic?.pairing && (
                                <div className="flex items-start gap-2 text-sm">
                                    <Utensils className="w-4 h-4 mt-0.5 shrink-0 text-[hsl(var(--accent))]" strokeWidth={1.5} />
                                    <div>
                                        <div className="co-caption mb-1">Abbinamenti</div>
                                        <div>{wine.organoleptic.pairing}</div>
                                    </div>
                                </div>
                            )}

                            {wine.producer_website && (
                                <div className="flex items-center gap-2 text-sm" data-testid="wine-detail-producer-website">
                                    <ExternalLink className="w-4 h-4 shrink-0 text-[hsl(var(--muted-foreground))]" strokeWidth={1.5} />
                                    <a
                                        href={wine.producer_website}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="underline underline-offset-4 decoration-1 hover:text-[hsl(var(--primary))] break-all"
                                    >
                                        Sito del produttore
                                    </a>
                                </div>
                            )}

                            <div className="pt-4 border-t border-[hsl(var(--border))]">
                                <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4">
                                    <div className="flex-1">
                                        <label className="co-caption mb-2 block">Formato bottiglia</label>
                                        <Select value={selectedFormat || ""} onValueChange={setSelectedFormat}>
                                            <SelectTrigger data-testid="wine-detail-format-select" className="w-full sm:w-48 bg-white">
                                                <SelectValue placeholder="Seleziona" />
                                            </SelectTrigger>
                                            <SelectContent>
                                                {wine.formats.map((f) => (
                                                    <SelectItem key={f.size} value={f.size}>
                                                        {f.size} L {f.size === "1.5" ? "(Magnum)" : f.size === "0.375" ? "(Demi)" : ""}
                                                    </SelectItem>
                                                ))}
                                            </SelectContent>
                                        </Select>
                                    </div>
                                    <div className="text-right">
                                        <div className="co-caption mb-1">Prezzo</div>
                                        {currentPrice !== null ? (
                                            <div className="co-price text-3xl" data-testid="wine-detail-price">
                                                € {currentPrice.toFixed(2)}
                                            </div>
                                        ) : (
                                            // Fase 6.8: listino pubblico sempre visibile;
                                            // se il prezzo effettivo è 0/mancante mostriamo "—".
                                            <div className="co-price text-3xl text-[hsl(var(--muted-foreground))]" data-testid="wine-detail-price-missing">
                                                —
                                            </div>
                                        )}
                                        {/* Riga sottile per trade/vip non verificati */}
                                        {(!canSeePrices || isTradeVipUnverified) && (
                                            <div className="mt-1 text-[10px] italic text-[hsl(var(--muted-foreground))]/70 tracking-wide" data-testid="wine-detail-trade-hint">
                                                Sei un cliente Trade/VIP? <a href="/login" className="underline hover:text-[hsl(var(--primary))]">Accedi</a>
                                            </div>
                                        )}
                                    </div>
                                </div>

                                {typeof currentFmt?.stock_quantity === "number" && (
                                    <div className="mt-3 co-caption" data-testid="wine-detail-stock">
                                        Giacenza {currentFmt.size} L: {currentFmt.stock_quantity} bottiglie
                                    </div>
                                )}
                                {currentFmt?.mexal_sku && (
                                    <div className="mt-2 text-xs font-mono text-[hsl(var(--muted-foreground))]" data-testid="wine-detail-sku">
                                        Cod. {currentFmt.mexal_sku}
                                    </div>
                                )}
                            </div>
                        </div>
                    </>
                )}
            </DialogContent>
        </Dialog>
    );
}
